import { Header, type HeaderAnalyticsEvent } from '@giffgaff/fe-design-system-ui'
import { Header as LegacyHeader } from '@fe-toolkit/web-components/ssr'
import { accountLinks } from '@fe-toolkit/utility'
import { customEvent } from '@fe-toolkit/eventing'

import { ACCOUNT_IMAGE_URL, LEGACY_BLACK_FRIDAY_HEADER_LINKS } from './fallbackChrome.content'
import { FallbackChromeFlags, showsNewChrome } from './fallbackChromeFlags'

import type { ChromeFlags } from '@/api/client-tracking-context/getChromeFlags'

// The published Header emits title-case `type` and `interaction` ('List', 'Click'), but the
// registered cta_interact Iglu schema is lowercase and a capitalised value becomes a Snowplow bad
// row. Same bridge as fe-common's CommonHeader, so fallback clicks land like the real header's.
export const trackHeaderEvent = ({ eventName, eventData }: HeaderAnalyticsEvent) => {
  customEvent(eventName, {
    ...eventData,
    type: eventData.type.toLowerCase() as Lowercase<typeof eventData.type>,
    interaction: eventData.interaction.toLowerCase() as Lowercase<typeof eventData.interaction>,
  })
}

// Degraded Header rendered when the fe_common remote is unavailable, in the variant fe-common
// would show (see fallbackChromeFlags for where the flags come from):
//   - `giffgaff-brand-refresh-2026` on: the feds-ui Header with its packaged navigation, account
//     links and auth URLs. `black-friday-master` or `black-friday-prereg` puts the packaged Black
//     Friday link first.
//   - off: the fe-toolkit Header with the app's static links and auth URLs, with the packaged
//     Black Friday category first for `black-friday-master`, or else the pre-registration link
//     first for `black-friday-prereg`. Black Friday never switches Headers (fe-common #63).
// Both forward `username`.
//
// Shared by BOTH fallback layers so the degraded UI is identical regardless of which fires:
//   - the errorLoadRemote registry (mf-offline-handling-plugin.ts) — load failure
//   - the FederatedComponentBoundary (layout.tsx) — render-time throw
//
// The registry path also hands it the remote's other props (the flags meant for CommonHeader);
// they are ignored in favour of the layout's kept server answer.
export default function FallbackHeader({ username }: { username?: string }) {
  return (
    <FallbackChromeFlags>{(flags) => <FallbackHeaderVariant {...flags} username={username} />}</FallbackChromeFlags>
  )
}

// Black Friday is hardcoded on for now rather than read from CTC.
const FallbackHeaderVariant = ({ username, ...flags }: ChromeFlags & { username?: string }) => {
  if (!showsNewChrome(flags)) {
    return (
      <LegacyHeader
        accountLinks={accountLinks}
        authUrlDashboard="/dashboard"
        authUrlLogIn="/auth/login"
        authUrlLogOut="/auth/logout"
        links={LEGACY_BLACK_FRIDAY_HEADER_LINKS}
        position="sticky"
        username={username}
      />
    )
  }

  return (
    <Header
      isBlackFriday
      accountImageUrl={ACCOUNT_IMAGE_URL}
      position="sticky"
      username={username}
      onAnalyticsEvent={trackHeaderEvent}
    />
  )
}
